/**
 * Mode Indicator Component
 *
 * Shows a subtle indicator of the current app mode.
 * Only visible in mock and demo modes, hidden in production.
 */

import React from 'react';
import { View, Text } from 'react-native';
import { APP_MODE, modeDescriptions, mode, hasActiveOverride } from '@/lib/config/app-mode';

/**
 * Compact mode badge for headers - the primary way to show mode.
 * Shows an "override" label when a persisted override is active so
 * testers always know the mode was manually changed.
 */
export function ModeBadge() {
  // Don't show in production mode (unless it's an override)
  const overrideActive = hasActiveOverride();
  if (mode.isProduction && !overrideActive) {
    return null;
  }

  const isMock = APP_MODE === 'mock';

  return (
    <View
      className={`px-2 py-0.5 rounded-full flex-row items-center gap-1 ${
        isMock ? 'bg-amber-500' : 'bg-blue-500'
      }`}
    >
      <Text className="text-white text-[10px] font-semibold uppercase tracking-wide">
        {APP_MODE}
      </Text>
      {overrideActive && (
        <Text className="text-white/70 text-[9px] font-medium">
          (override)
        </Text>
      )}
    </View>
  );
}

/**
 * Minimal text-only mode label for tight spaces
 */
export function ModeLabel() {
  if (mode.isProduction) {
    return null;
  }

  const isMock = APP_MODE === 'mock';
  const modeInfo = modeDescriptions[APP_MODE];

  return (
    <Text
      className={`text-[10px] font-semibold uppercase tracking-wide ${
        isMock ? 'text-amber-500' : 'text-blue-500'
      }`}
    >
      {modeInfo.emoji} {APP_MODE}
    </Text>
  );
}

/**
 * Legacy floating indicator - kept for backwards compatibility
 * @deprecated Use ModeBadge in header instead
 */
export function ModeIndicator() {
  // Return null - we now use ModeBadge in headers instead
  return null;
}
